"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.applicationInsightsConfigSchema = void 0;
var _zod = require("zod");
const applicationInsightsConfigSchema = exports.applicationInsightsConfigSchema = _zod.z.object({
  /**
   * Account ID
   */
  accountId: _zod.z.string().optional(),
  /**
   * Session renewal timeout in milliseconds
   * @default 1800000 (30 minutes)
   */
  sessionRenewalMs: _zod.z.coerce.number().optional().default(1800000),
  /**
   * Session expiration timeout in milliseconds
   * @default 86400000 (24 hours)
   */
  sessionExpirationMs: _zod.z.coerce.number().optional().default(86400000),
  /**
   * Maximum batch size in bytes
   * @default 10000
   */
  maxBatchSizeInBytes: _zod.z.coerce.number().optional().default(10000),
  /**
   * Maximum batch interval in milliseconds
   * @default 15000
   */
  maxBatchInterval: _zod.z.coerce.number().optional().default(15000),
  /**
   * Disable exception tracking
   * @default false
   */
  disableExceptionTracking: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Disable all telemetry collection
   * @default false
   */
  disableTelemetry: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Enable debug mode
   * @default false
   */
  enableDebug: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Logging level for console output
   * 0 = off, 1 = Critical errors only, 2 = Everything (errors & warnings)
   * @default 0
   */
  loggingLevelConsole: _zod.z.coerce.number().min(0).max(2).optional().default(0),
  /**
   * Logging level for telemetry
   * 0 = off, 1 = Critical errors only, 2 = Everything (errors & warnings)
   * @default 1
   */
  loggingLevelTelemetry: _zod.z.coerce.number().min(0).max(2).optional().default(1),
  /**
   * Diagnostic log interval in milliseconds
   * @default 10000
   */
  diagnosticLogInterval: _zod.z.coerce.number().optional().default(10000),
  /**
   * Sampling percentage for general telemetry (0-100)
   * @default 100
   */
  samplingPercentage: _zod.z.coerce.number().min(0).max(100).optional().default(100),
  /**
   * Auto track page visit time
   * @default false
   */
  autoTrackPageVisitTime: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Disable Ajax tracking
   * @default false
   */
  disableAjaxTracking: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Disable Fetch tracking
   * @default false
   */
  disableFetchTracking: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Exclude specific routes from automatic tracking (array of strings OR array of RegExp patterns)
   * Note: Must be all strings or all RegExp, not mixed
   */
  excludeRequestFromAutoTrackingPatterns: _zod.z.union([_zod.z.array(_zod.z.string()), _zod.z.array(_zod.z.instanceof(RegExp))]).optional(),
  /**
   * Override page view duration tracking
   * @default false
   */
  overridePageViewDuration: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Maximum Ajax calls per page view
   * @default 500
   */
  maxAjaxCallsPerView: _zod.z.coerce.number().optional().default(500),
  /**
   * Disable data loss analysis
   * @default true
   */
  disableDataLossAnalysis: _zod.z.coerce.boolean().optional().default(true),
  /**
   * Disable correlation headers
   * @default false
   */
  disableCorrelationHeaders: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Domains to exclude from correlation headers
   */
  correlationHeaderExcludedDomains: _zod.z.array(_zod.z.string()).optional(),
  /**
   * Domains to include for correlation headers
   */
  correlationHeaderDomains: _zod.z.array(_zod.z.string()).optional(),
  /**
   * Disable flushing telemetry on beforeunload/pagehide
   * @default false
   */
  disableFlushOnBeforeUnload: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Enable session storage buffer
   * @default true
   */
  enableSessionStorageBuffer: _zod.z.coerce.boolean().optional().default(true),
  /**
   * Disable cookie usage
   * @default false
   */
  isCookieUseDisabled: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Disable cookies usage (alias)
   * @default false
   */
  disableCookiesUsage: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Cookie domain for session tracking
   */
  cookieDomain: _zod.z.string().optional(),
  /**
   * Cookie path for session tracking
   * @default "/"
   */
  cookiePath: _zod.z.string().optional().default('/'),
  /**
   * Disable retry logic
   * @default false
   */
  isRetryDisabled: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Disable local/session storage usage
   * @default false
   */
  isStorageUseDisabled: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Disable Beacon API
   * @default true
   */
  isBeaconApiDisabled: _zod.z.coerce.boolean().optional().default(true),
  /**
   * Disable XMLHttpRequest
   * @default false
   */
  disableXhr: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Disable Beacon API on unload
   * @default false
   */
  onunloadDisableBeacon: _zod.z.coerce.boolean().optional().default(false),
  /**
   * SDK extension name
   */
  sdkExtension: _zod.z.string().optional(),
  /**
   * Enable browser link tracking
   * @default false
   */
  isBrowserLinkTrackingEnabled: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Application ID for cross-component correlation
   */
  appId: _zod.z.string().optional(),
  /**
   * Enable CORS correlation
   * @default false
   */
  enableCorsCorrelation: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Name prefix for telemetry items
   */
  namePrefix: _zod.z.string().optional(),
  /**
   * Enable automatic route tracking (for SPAs)
   * @default false
   */
  enableAutoRouteTracking: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Enable request header tracking
   * @default false
   */
  enableRequestHeaderTracking: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Enable response header tracking
   * @default false
   */
  enableResponseHeaderTracking: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Enable Ajax error status text
   * @default false
   */
  enableAjaxErrorStatusText: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Enable Ajax performance tracking
   * @default false
   */
  enableAjaxPerfTracking: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Maximum Ajax performance lookup attempts
   * @default 3
   */
  maxAjaxPerfLookupAttempts: _zod.z.coerce.number().optional().default(3),
  /**
   * Ajax performance lookup delay in milliseconds
   * @default 25
   */
  ajaxPerfLookupDelay: _zod.z.coerce.number().optional().default(25),
  /**
   * Distributed tracing mode
   * 0 = off, 1 = W3C, 2 = AI_AND_W3C
   * @default 1
   */
  distributedTracingMode: _zod.z.coerce.number().min(0).max(2).optional().default(1),
  /**
   * Enable unhandled promise rejection tracking
   * @default false
   */
  enableUnhandledPromiseRejectionTracking: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Disable instrumentation key validation
   * @default false
   */
  disableInstrumentationKeyValidation: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Enable performance manager
   * @default false
   */
  enablePerfMgr: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Default length for generating random session and user IDs
   * @default 22
   */
  idLength: _zod.z.coerce.number().optional().default(22),
  /**
   * Convert undefined values to user-defined value
   */
  convertUndefined: _zod.z.any().optional(),
  /**
   * Number of events that can be kept in memory
   * @default 10000
   */
  eventsLimitInMem: _zod.z.coerce.number().optional().default(10000),
  /**
   * Disable instrumentation key deprecation message
   * @default true
   */
  disableIkeyDeprecationMessage: _zod.z.coerce.boolean().optional().default(true),
  /**
   * Storage name prefix
   */
  storagePrefix: _zod.z.string().optional(),
  /**
   * Status codes that will cause event batches to be resent
   */
  retryCodes: _zod.z.array(_zod.z.number()).optional(),
  /**
   * Page unload events to ignore
   */
  disablePageUnloadEvents: _zod.z.array(_zod.z.string()).optional(),
  /**
   * Page show events to ignore
   */
  disablePageShowEvents: _zod.z.array(_zod.z.string()).optional(),
  /**
   * Endpoint URL for telemetry ingestion
   */
  endpointUrl: _zod.z.string().optional(),
  /**
   * Emit line delimited JSON
   * @default false
   */
  emitLineDelimitedJson: _zod.z.coerce.boolean().optional().default(false),
  /**
   * Enable debug exceptions
   * @default false
   */
  enableDebugExceptions: _zod.z.coerce.boolean().optional().default(false)
});
//# sourceMappingURL=applicationInsightsConfigSchema.js.map